import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import BrandLogo from "../components/BrandLogo";
import PaymentInstructionsView from "../components/PaymentInstructionsView";
import { CheckCircle, XCircle, Clock, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";

const OFFER_TYPE_LABEL = {
    pay_for_deletion: "Pay-for-Deletion Settlement Offer",
    lump_sum: "Lump-Sum Settlement Offer",
    payment_plan: "Payment-Plan Settlement Offer",
};

export default function PublicOffer() {
    const { token } = useParams();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signerName, setSignerName] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'accept'|'decline'|'counter'
    const [counterAmount, setCounterAmount] = useState("");
    const [counterTerms, setCounterTerms] = useState("");
    const [counterResult, setCounterResult] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/offers/public/${token}`);
            setOffer(data);
            setSignerName((s) => s || data.recipient_name);
        } catch (e) {
            setError(formatApiError(e));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const respond = async (action) => {
        setSubmitting(true);
        try {
            await api.post(`/offers/public/${token}/respond`, { action, signer_name: signerName, note });
            toast.success(action === "accept" ? "Offer accepted" : "Offer declined");
            setConfirmAction(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSubmitting(false); }
    };

    const submitCounter = async () => {
        const amt = parseFloat(counterAmount);
        if (!amt || amt <= 0) { toast.error("Enter a valid counter amount"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/offers/public/${token}/counter`, {
                offer_amount: amt,
                payment_terms: counterTerms || null,
                note: note || null,
                signer_name: signerName,
            });
            setCounterResult(data);
            toast.success("Counter-offer sent");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-500 font-sans">Loading offer…</div>;
    }
    if (error || !offer) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
                <Card className="max-w-md p-8 text-center">
                    <ShieldWarning size={32} weight="duotone" className="mx-auto text-red-600" />
                    <h1 className="font-serif text-2xl mt-4">Offer not available</h1>
                    <p className="text-sm text-slate-600 mt-2">{error || "This offer could not be found."}</p>
                </Card>
            </div>
        );
    }

    const expires = new Date(offer.expires_at);
    const isExpired = expires < new Date();
    const status = isExpired && offer.status === "sent" ? "expired" : offer.status;
    const amount = offer.offer_amount;
    const owed = offer.case?.amount_owed || 0;
    const pct = owed ? (amount / owed * 100) : 0;

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {/* Header */}
            <header className="bg-[#0F1E3D] text-white">
                <div className="max-w-2xl mx-auto px-6 py-8">
                    <div className="flex items-center gap-2">
                        <BrandLogo height={36} className="brightness-0 invert" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-6">Settlement offer · token #{token.slice(0,8)}</p>
                    <h1 className="font-serif text-4xl mt-2">{OFFER_TYPE_LABEL[offer.offer_type] || "Settlement Offer"}</h1>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-6 py-10 space-y-6" data-testid="public-offer">
                {/* Status banner */}
                <StatusBanner status={status} respondedAt={offer.responded_at} response={offer.response} />

                {status === "accepted" && (
                    <PaymentInstructionsView
                        instructions={offer.payment_instructions}
                        amount={amount}
                    />
                )}

                {offer.case_settled && (
                    <Card className="p-6 bg-emerald-50 border-emerald-200" data-testid="paid-card">
                        <div className="flex items-start gap-3">
                            <CheckCircle size={24} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-emerald-900">Settled — payment received</h3>
                                <p className="text-sm text-emerald-800 mt-1">
                                    The creditor confirmed receipt of ${amount.toLocaleString()}. This case is closed.
                                </p>
                            </div>
                        </div>
                    </Card>
                )}

                <Card className="p-6 space-y-4">
                    <p className="text-sm text-slate-700">Dear <span className="font-semibold">{offer.recipient_name}</span>,</p>
                    <p className="text-sm text-slate-700">A formal settlement offer has been prepared for the following case:</p>

                    <div className="border-t border-b border-slate-200 py-4 space-y-2 text-sm">
                        <Row label="Case" value={offer.case?.title} />
                        <Row label="Creditor of record" value={offer.case?.creditor_name} />
                        {offer.case?.case_number && <Row label="Case / account #" value={offer.case.case_number} />}
                        {offer.case?.jurisdiction && <Row label="Jurisdiction" value={offer.case.jurisdiction} />}
                        <Row label="Amount currently owed" value={`$${owed.toLocaleString()}`} />
                        <Row label="Offered settlement" value={
                            <span className="text-[#0F1E3D] font-semibold">
                                ${amount.toLocaleString()} <span className="text-slate-500 font-normal text-xs">({pct.toFixed(0)}% of balance)</span>
                            </span>
                        } />
                        {offer.payment_terms && <Row label="Payment terms" value={offer.payment_terms} />}
                        <Row label="Response deadline" value={formatDateUS(expires)} />
                    </div>

                    {offer.offer_type === "pay_for_deletion" && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-slate-700">
                            <p className="font-semibold text-[#0F1E3D] mb-1">Pay-for-Deletion clause</p>
                            Upon acceptance and payment, the creditor agrees to delete the tradeline from all three major credit bureaus
                            (Equifax, Experian, TransUnion) and to confirm deletion in writing.
                        </div>
                    )}

                    {offer.custom_message && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm italic text-slate-700">
                            &ldquo;{offer.custom_message}&rdquo;
                        </div>
                    )}

                    <p className="text-xs text-slate-500 pt-3 border-t border-slate-200">
                        Prepared at the client&apos;s direction. LienLift Direct is a non-legal document &amp; information service, not a law firm.
                        Acceptance is binding only upon written confirmation between the parties.
                    </p>
                </Card>

                {/* Response area */}
                {status === "sent" && !confirmAction && !counterResult && (
                    <Card className="p-6 space-y-3">
                        <h3 className="font-semibold">Your response</h3>
                        <p className="text-xs text-slate-500">Choose an action below. The case party will be notified immediately.</p>
                        <div className="grid grid-cols-3 gap-3">
                            <Button
                                onClick={() => setConfirmAction("accept")}
                                data-testid="public-accept-btn"
                                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-base"
                            >
                                <CheckCircle size={18} className="mr-2" weight="fill" /> Accept
                            </Button>
                            <Button
                                onClick={() => setConfirmAction("counter")}
                                data-testid="public-counter-btn"
                                className="h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-base text-white"
                            >
                                Counter
                            </Button>
                            <Button
                                onClick={() => setConfirmAction("decline")}
                                data-testid="public-decline-btn"
                                variant="outline"
                                className="h-12 border-red-200 text-red-700 hover:bg-red-50 text-base"
                            >
                                <XCircle size={18} className="mr-2" weight="fill" /> Decline
                            </Button>
                        </div>
                    </Card>
                )}

                {status === "sent" && confirmAction === "counter" && !counterResult && (
                    <Card className="p-6 space-y-4" data-testid="public-counter-form">
                        <h3 className="font-semibold">Send a counter-offer</h3>
                        <p className="text-xs text-slate-500">Propose a different amount or terms. The case owner will receive an email with your counter.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="ca">Counter amount (USD) *</Label>
                                <Input data-testid="counter-amount" id="ca" type="number" min="1" step="0.01" value={counterAmount} onChange={(e) => setCounterAmount(e.target.value)} className="mt-1.5 h-11 font-mono" />
                            </div>
                            <div>
                                <Label htmlFor="sn2">Your name (signature)</Label>
                                <Input data-testid="counter-signer" id="sn2" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1.5 h-11" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="ct">Payment terms (optional)</Label>
                            <Input data-testid="counter-terms" id="ct" value={counterTerms} onChange={(e) => setCounterTerms(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. 50/50 split paid over 90 days" />
                        </div>
                        <div>
                            <Label htmlFor="cn2">Note (optional)</Label>
                            <Textarea data-testid="counter-note" id="cn2" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Context for your counter." />
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={submitting} className="h-11">Back</Button>
                            <Button onClick={submitCounter} disabled={submitting || !counterAmount || !signerName.trim()} data-testid="counter-submit-btn" className="h-11 flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]">
                                {submitting ? "Sending…" : "Send counter-offer"}
                            </Button>
                        </div>
                    </Card>
                )}

                {counterResult && (
                    <Card className="p-6 space-y-3 bg-blue-50/40 border-blue-200" data-testid="counter-result">
                        <h3 className="font-semibold text-[#0F1E3D]">Counter-offer sent</h3>
                        <p className="text-sm text-slate-700">
                            The case owner has been emailed your counter of <strong>${counterResult.offer_amount.toLocaleString()}</strong>.
                            They can accept, decline or counter again at the link below.
                        </p>
                        <a href={counterResult.public_url} className="text-xs font-mono text-[#2563EB] break-all hover:underline">
                            {counterResult.public_url}
                        </a>
                    </Card>
                )}

                {status === "sent" && confirmAction && confirmAction !== "counter" && (
                    <Card className="p-6 space-y-4" data-testid="public-confirm-form">
                        <h3 className="font-semibold">
                            {confirmAction === "accept" ? "Confirm acceptance" : "Confirm decline"}
                        </h3>
                        <div>
                            <Label htmlFor="sn">Your full name (electronic signature)</Label>
                            <Input data-testid="public-signer-name" id="sn" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="nt">Note (optional)</Label>
                            <Textarea data-testid="public-note" id="nt" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder={confirmAction === "decline" ? "Briefly explain why or counter-offer." : "Any additional terms or contact info."} />
                        </div>
                        <p className="text-xs text-slate-500">
                            By clicking confirm, you acknowledge you have authority to {confirmAction} this offer on behalf of {offer.recipient_name}.
                        </p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={submitting} className="h-11">Back</Button>
                            <Button
                                onClick={() => respond(confirmAction)}
                                disabled={submitting || !signerName.trim()}
                                data-testid="public-confirm-btn"
                                className={`h-11 flex-1 ${confirmAction === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
                            >
                                {submitting ? "Submitting…" : confirmAction === "accept" ? "Confirm acceptance" : "Confirm decline"}
                            </Button>
                        </div>
                    </Card>
                )}

                <footer className="text-center text-xs text-slate-500 pt-6">
                    Powered by LienLift Direct · A non-legal judgment information &amp; document service.
                </footer>
            </main>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline gap-4">
            <span className="text-xs uppercase tracking-[0.14em] text-slate-500 w-44 shrink-0">{label}</span>
            <span className="text-slate-900">{value}</span>
        </div>
    );
}

function StatusBanner({ status, respondedAt, response }) {
    const map = {
        sent: { Icon: Clock, tone: "bg-blue-50 border-blue-200 text-blue-900", label: "Awaiting your response" },
        accepted: { Icon: CheckCircle, tone: "bg-emerald-50 border-emerald-200 text-emerald-900", label: "Accepted" },
        declined: { Icon: XCircle, tone: "bg-rose-50 border-rose-200 text-rose-900", label: "Declined" },
        countered: { Icon: Clock, tone: "bg-blue-50 border-blue-200 text-blue-900", label: "Countered — awaiting case owner's response" },
        expired: { Icon: ShieldWarning, tone: "bg-amber-50 border-amber-200 text-amber-900", label: "Offer expired" },
    };
    const cfg = map[status] || map.sent;
    return (
        <div className={`flex items-center gap-3 p-4 rounded-md border ${cfg.tone}`} data-testid={`status-${status}`}>
            <cfg.Icon size={20} weight="duotone" />
            <div className="flex-1">
                <p className="font-semibold text-sm">{cfg.label}</p>
                {respondedAt && (
                    <p className="text-xs opacity-75 mt-0.5">
                        {response?.signer_name ? `${response.signer_name} · ` : ""}{new Date(respondedAt).toLocaleString()}
                        {response?.note ? ` — "${response.note}"` : ""}
                    </p>
                )}
            </div>
        </div>
    );
}
